import { Button, Typography, Divider, Box } from "@mui/material";
import { useEffect, useState } from "react";
import { useHttp } from "../../hooks/useHttp";
import { Pipeline, PipelineSteps, Task, TaskStatus, TaskStatusType } from "../../models/tasks";
import "./Admin.scss";

interface AdminInterface {
  authenticated: boolean;
}

export const Admin = ({ authenticated }: AdminInterface) => {
  const [tasks, setTasks] = useState<Record<string, Task[]>>({});
  const [trash, setTrash] = useState<string[]>([]);

  const { trigger: fetchTasks } = useHttp<Task[]>("/lungsTasks/all");

  useEffect(() => {
    if (authenticated) {
      const eventSource = new EventSource(process.env.REACT_APP_API_URL + "/tasks/updates/all");

      eventSource.onmessage = (event) => {
        const event_str: string[] = event.data.split("<SEP>");
        const task_id: string = event_str[1];
        const status: TaskStatus = {
          id: event_str[0],
          status: event_str[2] as TaskStatusType,
          worker: event_str[3],
          message: event_str[4],
          createdAt: event_str[5],
          processingTime: event_str[6] as unknown as number,
          stage: event_str[7] as PipelineSteps,
          pipeline: event_str[9] as Pipeline,
        };
        console.log("New status");
        setTasks((pevTasks) => {
          const updatedGroupedTasks = { ...pevTasks };

          for (const [sessionId, tasks] of Object.entries(updatedGroupedTasks)) {
            const taskIndex = tasks.findIndex((task) => task.id === task_id);

            if (taskIndex !== -1) {
              const task = tasks[taskIndex];
              if (!task.statuses.find((s) => s.id === status.id)) {
                task.statuses.push(status);
              }
              tasks[taskIndex] = { ...task };
              updatedGroupedTasks[sessionId] = [...tasks];
              break;
            }
          }

          return updatedGroupedTasks;
        });
      };

      return () => {
        eventSource.close();
      };
    }
  }, [authenticated]);

  const refetchTasks = () => {
    fetchTasks().then((r: Task[]) => {
      if (r) {
        const grouped = r.reduce((acc: Record<string, Task[]>, task) => {
          if (!acc[task.sessionId]) {
            acc[task.sessionId] = [];
          }
          acc[task.sessionId].push(task);
          return acc;
        }, {});
        setTasks(grouped);
      }
    });
  };

  useEffect(() => {
    refetchTasks();
  }, [fetchTasks]);

  const moveToTrash = () => {
    const current_task_ids = Object.values(tasks)
      .map((tasks) => tasks.map((task) => task.id))
      .flat();
    setTrash((prev) => [...prev, ...current_task_ids]);
  };

  const assignColor = (status: TaskStatusType) => {
    switch (status) {
      case TaskStatusType.COMPLETED:
        return "lightgreen";
      case TaskStatusType.FAILED:
        return "red";
      case TaskStatusType.BETWEEN_STAGES:
        return "orange";
      case TaskStatusType.PROCESSING:
        return "yellow";
      case TaskStatusType.TERMINATED:
        return "violet";
      default:
        return "gray";
    }
  };

  return (
    <div className="page admin">
      <Box className="admin-buttons">
        <Button onClick={moveToTrash}>Thrash completed</Button>
        <Button onClick={() => setTrash([])}>Clear thrash</Button>
        <Button onClick={refetchTasks}>Refetch</Button>
      </Box>
      {Object.keys(tasks)
        .sort((a: string, b: string) => {
          const dateA = new Date(tasks[a][tasks[a].length - 1].createdAt);
          const dateB = new Date(tasks[b][tasks[b].length - 1].createdAt);

          return dateA > dateB ? -1 : dateA < dateB ? 1 : 0;
        })
        .map((sessionId: string, index: number) => {
          const taskIds = tasks[sessionId].map((task) => task.id);
          const allInTrash = taskIds.every((taskId) => trash.includes(taskId));
          if (allInTrash) return null;
          else
            return (
              <Box key={index}>
                <Divider />
                <div className="admin-session">
                  <Typography variant="body2">Session: {sessionId}</Typography>
                  {tasks[sessionId].map((task, task_index: number) => {
                    if (trash.includes(task.id)) return null;
                    else
                      return (
                        <div className="admin-session-task" key={task_index}>
                          {task.statuses.length > 0 && (
                            <div
                              className="admin-session-task-status"
                              style={{
                                backgroundColor: assignColor(task.statuses[task.statuses.length - 1].status),
                              }}
                            >
                              <Typography>
                                {Math.round(task.statuses[task.statuses.length - 1].processingTime * 10) / 10}s
                              </Typography>
                              <Typography>{task.statuses[task.statuses.length - 1].stage}</Typography>
                            </div>
                          )}
                        </div>
                      );
                  })}
                </div>
              </Box>
            );
        })}
    </div>
  );
};
